import React, { useEffect, useState } from "react";
import axiosInstance from '../networking/axiosconfig';
import { Calendar, Clock, User, Mail, FileText, CheckCircle2, XCircle, History as HistoryIcon } from 'lucide-react';
import Spinner from "../component/Spinner";
import ProfileAvatar from '../component/ProfileAvtar';
import Navbar from './Navbar';

const HistoryLeaveList = () => {
    const [device, setDevice] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sortConfig, setSortConfig] = useState({ key: "date", direction: "desc" });

    const sortedDevice = [...device].sort((a, b) => {
        let dateA = new Date(a.date).getTime();
        let dateB = new Date(b.date).getTime();
        return sortConfig.direction === "asc" ? dateA - dateB : dateB - dateA;
    });

    const handleSort = () => {
        setSortConfig((prev) => ({
            key: "date",
            direction: prev.direction === "asc" ? "desc" : "asc",
        }));
    };

    const apiUrl = `/api/leave`;

    const groupLeavesByGroupId = (leaves) => {
        const groupedLeaves = {};

        leaves.forEach((leave) => {
            const groupId = leave.leave_group_id;

            if (!groupedLeaves[groupId]) {
                groupedLeaves[groupId] = {
                    ...leave,
                    dates: [new Date(leave.date)],
                };
            } else {
                groupedLeaves[groupId].dates.push(new Date(leave.date));
            }
        });

        return Object.values(groupedLeaves).map((leave) => ({
            ...leave,
            start_date: leave.dates.length > 0 ? leave.dates[0] : null,
            end_date: leave.dates.length > 1 ? leave.dates[leave.dates.length - 1] : leave.dates[0],
            formatted_date: `${leave.dates[0].toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
            })} - ${leave.dates[leave.dates.length - 1].toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
            })}`,
        }));
    };

    const fetchLeaveData = async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get(apiUrl);
            if (res.data && Array.isArray(res.data.data)) {
                const groupedLeaves = groupLeavesByGroupId(res.data.data);
                setDevice(groupedLeaves);
            } else {
                console.error("Invalid API Response:", res.data);
            }
        } catch (err) {
            console.error("API Error:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLeaveData();
    }, []);

    const getStatusBadge = (status) => {
        if (status === 1) {
            return (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                    <CheckCircle2 size={14} />
                    Approved
                </span>
            );
        } else if (status === 2) {
            return (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800">
                    <XCircle size={14} />
                    Rejected
                </span>
            );
        }
        return (
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800">
                <Clock size={14} />
                Pending
            </span>
        );
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
            <Navbar />
            <div className="p-6">
                <div className="max-w-7xl mx-auto">
                    {/* Header */}
                    <div className="bg-white rounded-2xl shadow-xl p-6  bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-800 text-white">
                        <div className="flex items-center justify-between">
                            <div>
                                <h1 className="text-3xl font-bold  flex items-center gap-3">
                                    <HistoryIcon className="" size={36} />
                                    Leave History
                                </h1>
                                <p className=" mt-2">View all processed leave requests</p>
                            </div>
                            <div className="bg-blue-100 px-4 py-2 rounded-lg">
                                <p className="text-sm text-blue-700 font-semibold">
                                    Total Records: {device.length}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Leave Cards */}
                    {loading ? (
                        <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
                            <Spinner size={40} color="#3b82f6" loading={loading} />
                            <p className="text-gray-600 mt-4">Loading leave history...</p>
                        </div>
                    ) : sortedDevice.length === 0 ? (
                        <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
                            <HistoryIcon className="mx-auto text-gray-400 mb-4" size={48} />
                            <p className="text-gray-600 text-lg">No leave history found</p>
                            <p className="text-gray-500 text-sm mt-2">Leave requests will appear here once processed</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 gap-6">
                            {sortedDevice.map((element, index) => (
                                <div
                                    key={element.leave_id_PK}
                                    className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow"
                                >
                                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                                        {/* Employee Info */}
                                        <div className="flex items-center gap-4 flex-1">
                                            <ProfileAvatar
                                                className="h-16 w-16"
                                                url={element.user_profile_img}
                                                name={`${element.first_name || "User"} ${element.last_name || ""}`}
                                            />
                                            <div className="flex-1">
                                                <h3 className="text-lg font-bold text-gray-800">
                                                    {element.first_name || "N/A"} {element.last_name || ""}
                                                </h3>
                                                <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-gray-600">
                                                    <div className="flex items-center gap-1">
                                                        <Mail size={16} />
                                                        <span>{element.email}</span>
                                                    </div>
                                                    <div className="flex items-center gap-1">
                                                        <Calendar size={16} />
                                                        <span>{element.formatted_date || "N/A"}</span>
                                                    </div>
                                                    <div className="flex items-center gap-1">
                                                        <FileText size={16} />
                                                        <span className="font-medium">{element.leave_name}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Status */}
                                        <div className="flex flex-col items-start md:items-end gap-2">
                                            {getStatusBadge(element.status)}
                                            {(element.status === 1 || element.status === 2) && (
                                                <p className="text-xs text-gray-500">
                                                    {element.status === 1 ? "Approved" : "Rejected"} by:{" "}
                                                    <span className="font-semibold text-gray-700">
                                                        {element.rejected_by_first_name || "N/A"}{" "}
                                                        {element.rejected_by_last_name || ""}
                                                    </span>
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default HistoryLeaveList;
